import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { toast } from 'sonner'
import { CalendarClock, Info } from 'lucide-react'
import { useClinic } from '@/hooks/use-clinic'
import { getStats } from '@/lib/clinic/selectors'
import { formatPhone } from '@/lib/clinic/format'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { PatientAvatar } from '@/components/app/patient-avatar'
import { PatientPicker } from '@/components/app/patient-picker'
import { ChipButton, CheckRow, FormField } from '@/components/app/form-bits'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import {
    QUICK_FOLLOW_UP_OPTIONS,
    addDays,
    applyQuickDate,
    formatTime,
    formatWeekday,
    isISODate
} from '@/lib/clinic/dates'

interface FollowUpDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    patientId: string | null
    followUpId: string | null
    visitId: string | null
    onRequestReminder: (patientId: string, followUpId: string) => void
}

export const FollowUpDialog: FC<FollowUpDialogProps> = ({
    open,
    onOpenChange,
    patientId: initialPatientId,
    followUpId,
    visitId,
    onRequestReminder
}): ReactNode => {
    const { data, index, today, saveFollowUp } = useClinic()
    const existing = followUpId ? index.followUpsById.get(followUpId) ?? null : null
    const [patientId, setPatientId] = useState<string | null>(existing?.patientId ?? initialPatientId)
    const patient = patientId ? index.patientsById.get(patientId) ?? null : null
    const lastVisit = patient ? index.visitsByPatient.get(patient.id)?.[0] ?? null : null
    const pending = !existing && patient ? getStats(index, patient.id).pendingFollowUp : null

    const [dueDate, setDueDate] = useState(existing?.dueDate ?? pending?.dueDate ?? (today ? addDays(today, data.settings.followUp.defaultIntervalDays) : ''))
    const [dueTime, setDueTime] = useState(existing?.dueTime ?? pending?.dueTime ?? '')
    const [reason, setReason] = useState(existing?.reason ?? pending?.reason ?? '')
    const [notes, setNotes] = useState(existing?.notes ?? pending?.notes ?? '')
    const [confirmed, setConfirmed] = useState(Boolean(existing?.confirmedAt ?? pending?.confirmedAt))
    const [error, setError] = useState<string | null>(null)

    const choosePatient = (id: string): void => {
        setPatientId(id)
        setError(null)

        const pick = index.patientsById.get(id)
        const pickPending = pick ? getStats(index, pick.id).pendingFollowUp : null

        if (pickPending) {
            setDueDate(pickPending.dueDate)
            setDueTime(pickPending.dueTime ?? '')
            setReason(pickPending.reason)
            setNotes(pickPending.notes)
            setConfirmed(Boolean(pickPending.confirmedAt))
        }
    }

    const submit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        if (!patient) {
            setError("Choose the patient first.")

            return
        }

        if (!isISODate(dueDate)) {
            setError("Choose the returning date.")

            return
        }

        const unchanged = existing?.dueDate === dueDate || pending?.dueDate === dueDate

        if (dueDate < today && !unchanged) {
            setError("The returning date can't be in the past.")

            return
        }

        const result = saveFollowUp({
            patientId: patient.id,
            visitId: existing?.visitId ?? pending?.visitId ?? visitId ?? lastVisit?.id ?? null,
            dueDate,
            dueTime: dueTime || null,
            reason,
            notes,
            confirmed
        }, existing?.id)

        onOpenChange(false)

        const when = `${formatWeekday(dueDate)}${dueTime ? `, ${formatTime(dueTime)}` : ''}`
        const title = existing || result.merged ? `Follow-up updated for ${patient.name}` : `Follow-up added for ${patient.name}`

        toast.success(title, {
            description: result.merged ? `The existing follow-up was moved to ${when}, so there is no duplicate.` : `Returning on ${when}.`,
            action: {
                label: 'Remind on WhatsApp',
                onClick: () => onRequestReminder(patient.id, result.followUp.id)
            }
        })
    }

    const reasonOptions = Array.from(new Set([
        ...(lastVisit?.treatment ? [lastVisit.treatment] : []),
        ...data.treatments.map(item => item.name)
    ]))

    return (
        <Dialog open={open} onOpenChange={next => onOpenChange(next)}>
            <DialogContent className='max-h-[92vh] overflow-y-auto sm:max-w-lg'>
                <DialogHeader>
                    <DialogTitle className='flex items-center gap-2 text-lg'>
                        <CalendarClock className='size-5 text-primary' aria-hidden='true' />
                        {existing ? 'Edit follow-up' : 'Add follow-up'}
                    </DialogTitle>

                    <DialogDescription>
                        Set when the patient should come back to the clinic.
                    </DialogDescription>
                </DialogHeader>

                {!patient ? (
                    <div className='grid gap-2'>
                        <PatientPicker autoFocus onSelect={item => choosePatient(item.id)} />

                        {error && (
                            <p role='alert' className='text-xs font-medium text-destructive'>
                                {error}
                            </p>
                        )}
                    </div>
                ) : (
                    <form onSubmit={submit} className='grid gap-4' noValidate>
                        <div className='flex items-center gap-3 rounded-xl border bg-muted/40 p-3'>
                            <PatientAvatar name={patient.name} seed={patient.id} />

                            <div className='min-w-0 flex-1'>
                                <p className='truncate text-sm font-semibold text-foreground'>
                                    {patient.name}
                                </p>

                                <p className='truncate text-xs text-muted-foreground'>
                                    {patient.id} · {formatPhone(patient.phone)}
                                </p>
                            </div>

                            {!existing && !initialPatientId && (
                                <Button type='button' variant='ghost' size='sm' onClick={() => setPatientId(null)}>
                                    Change
                                </Button>
                            )}
                        </div>

                        {pending && (
                            <div className='flex gap-2.5 rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900'>
                                <Info className='mt-0.5 size-4 shrink-0' aria-hidden='true' />

                                <p>
                                    {patient.name} already has a follow-up on <strong>{formatWeekday(pending.dueDate)}</strong>. Saving updates it, so the patient never has two open follow-ups.
                                </p>
                            </div>
                        )}

                        <FormField label='Returning date' htmlFor='follow-up-date' required>
                            <Input
                                id='follow-up-date'
                                type='date'
                                value={dueDate}
                                min={existing || pending ? undefined : today}
                                onChange={event => {
                                    setDueDate(event.target.value)
                                    setError(null)
                                }}
                                aria-invalid={Boolean(error)}
                            />

                            <div className='flex flex-wrap gap-1.5 pt-1'>
                                {QUICK_FOLLOW_UP_OPTIONS.map(option => {
                                    const value = applyQuickDate(today, option)

                                    return (
                                        <ChipButton
                                            key={option.label}
                                            active={dueDate === value}
                                            onClick={() => {
                                                setDueDate(value)
                                                setError(null)
                                            }}
                                        >
                                            {option.label}
                                        </ChipButton>
                                    )
                                })}
                            </div>
                        </FormField>

                        <div className='grid gap-4 sm:grid-cols-[9rem_1fr]'>
                            <FormField label='Time' htmlFor='follow-up-time' hint='Optional'>
                                <Input id='follow-up-time' type='time' value={dueTime} onChange={event => setDueTime(event.target.value)} />
                            </FormField>

                            <FormField label='Reason or planned treatment' htmlFor='follow-up-reason'>
                                <Input
                                    id='follow-up-reason'
                                    list='follow-up-reason-options'
                                    value={reason}
                                    onChange={event => setReason(event.target.value)}
                                    placeholder='e.g. Root canal: second sitting'
                                />

                                <datalist id='follow-up-reason-options'>
                                    {reasonOptions.map(option => (
                                        <option key={option} value={option} />
                                    ))}
                                </datalist>
                            </FormField>
                        </div>

                        <FormField label='Notes' htmlFor='follow-up-notes' hint='Visible to clinic staff only.'>
                            <Textarea
                                id='follow-up-notes'
                                value={notes}
                                onChange={event => setNotes(event.target.value)}
                                rows={2}
                                placeholder='Anything the receptionist should know before calling'
                            />
                        </FormField>

                        <CheckRow
                            id='follow-up-confirmed'
                            checked={confirmed}
                            onCheckedChange={setConfirmed}
                            label='Patient has confirmed this date'
                            description='Tick this after speaking to the patient on the phone or WhatsApp.'
                        />

                        {error && (
                            <p role='alert' className='text-sm font-medium text-destructive'>
                                {error}
                            </p>
                        )}

                        <DialogFooter>
                            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                                Cancel
                            </Button>

                            <Button type='submit'>
                                {existing || pending ? 'Update follow-up' : 'Save follow-up'}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    )
}
